import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Clock, CheckCircle, Loader2, Calendar, MapPin, Phone, Star, User, X, Send, AlertCircle, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  
  // Review Modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  
  // Usta Detail Modal
  const [showUstaModal, setShowUstaModal] = useState(false);
  const [selectedUsta, setSelectedUsta] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      navigate('/');
      return;
    }
    
    const user = localStorage.getItem('user_data');
    if (user) {
      setUserData(JSON.parse(user));
    }
    
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/service-request/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Talepler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Beklemede', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, iconColor: 'text-amber-600' },
      assigned: { label: 'Usta Atandı', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: User, iconColor: 'text-blue-600' },
      in_progress: { label: 'Devam Ediyor', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Wrench, iconColor: 'text-purple-600' },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, iconColor: 'text-green-600' },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-800 border-red-200', icon: X, iconColor: 'text-red-600' }
    };
    return configs[status] || configs.pending;
  };

  const handleOpenReview = (request) => {
    setSelectedRequest(request);
    setReviewRating(5);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedRequest || !reviewComment.trim()) return;
    
    setReviewLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/customer/review`, {
        request_id: selectedRequest.id,
        rating: reviewRating,
        comment: reviewComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Yorumunuz kaydedildi! Admin onayından sonra yayınlanacak.');
      setShowReviewModal(false);
      fetchRequests();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.detail || 'Yorum gönderilemedi'));
    } finally {
      setReviewLoading(false);
    }
  };

  const handleViewUsta = (request) => {
    if (request.usta_name) {
      setSelectedUsta({
        name: request.usta_name,
        phone: request.usta_phone,
        photo: request.usta_photo,
        rating: request.usta_rating,
        reviewCount: request.usta_review_count
      });
      setShowUstaModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EDF6F9] to-[#E0F2F4]">
      <Navbar />

      <section className="py-8 md:py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg border border-[#B3E0E6]/30 p-6 md:p-8 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#264653]" data-testid="dashboard-title">
                  Hesabım
                </h1>
                {userData && (
                  <p className="text-lg text-[#5F7D8B] mt-1">
                    Hoş geldiniz, <span className="font-medium text-[#006D77]">{userData.full_name || userData.phone_number}</span>
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => navigate('/hizmetler')}
                  className="bg-[#006D77] hover:bg-[#005861] text-white"
                  data-testid="new-request-btn"
                >
                  + Yeni Talep
                </Button>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="border-[#E29578] text-[#E29578] hover:bg-[#E29578] hover:text-white"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B3E0E6]/30 text-center">
              <p className="text-2xl font-bold text-[#264653]">{requests.length}</p>
              <p className="text-sm text-[#5F7D8B]">Toplam Talep</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B3E0E6]/30 text-center">
              <p className="text-2xl font-bold text-amber-600">{requests.filter(r => r.status === 'pending').length}</p>
              <p className="text-sm text-[#5F7D8B]">Beklemede</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B3E0E6]/30 text-center">
              <p className="text-2xl font-bold text-blue-600">{requests.filter(r => r.status === 'assigned' || r.status === 'in_progress').length}</p>
              <p className="text-sm text-[#5F7D8B]">Devam Eden</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-[#B3E0E6]/30 text-center">
              <p className="text-2xl font-bold text-green-600">{requests.filter(r => r.status === 'completed').length}</p>
              <p className="text-sm text-[#5F7D8B]">Tamamlanan</p>
            </div>
          </div>

          {/* Requests List */}
          <div className="bg-white rounded-2xl shadow-lg border border-[#B3E0E6]/30 overflow-hidden">
            <div className="bg-gradient-to-r from-[#006D77] to-[#0A9396] px-6 py-4">
              <h2 className="text-xl font-heading font-semibold text-white">
                Servis Taleplerim
              </h2>
            </div>

            <div className="p-4 md:p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#006D77]" />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#E0F2F4] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wrench className="w-10 h-10 text-[#006D77]" />
                  </div>
                  <p className="text-[#5F7D8B] mb-4 text-lg">Henüz servis talebiniz bulunmuyor.</p>
                  <Button
                    onClick={() => navigate('/hizmetler')}
                    className="bg-[#E29578] hover:bg-[#D67A56] text-white px-8"
                    data-testid="create-request-button"
                  >
                    İlk Talebi Oluştur
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request, index) => {
                    const statusConfig = getStatusConfig(request.status);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div
                        key={request.id || index}
                        className="border border-[#B3E0E6] rounded-xl p-5 hover:shadow-lg transition-all bg-gradient-to-r from-white to-[#FAFFFE]"
                        data-testid={`request-${index}`}
                      >
                        {/* Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-heading font-semibold text-[#264653] capitalize">
                                {request.category?.replace('_', ' ')} Servisi
                              </h3>
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                                <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.iconColor}`} />
                                {statusConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-[#94A3B8]">
                              Talep No: #{request.id?.slice(-8).toUpperCase()}
                            </p>
                          </div>
                          
                          {/* Amount if completed */}
                          {request.amount_charged && (
                            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
                              <p className="text-xs text-green-600">Ödenen Tutar</p>
                              <p className="text-xl font-bold text-green-700">{request.amount_charged} ₺</p>
                            </div>
                          )}
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-[#5F7D8B] mb-4">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#006D77]" />
                            <span>{request.address}, {request.district}, {request.city}</span>
                          </div>
                          {request.scheduled_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-[#006D77]" />
                              <span>{request.scheduled_date} {request.scheduled_time && `- ${request.scheduled_time}`}</span>
                            </div>
                          )}
                        </div>

                        {/* Assigned Usta */}
                        {request.usta_name && (
                          <div 
                            className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 cursor-pointer hover:bg-blue-100 transition-all"
                            onClick={() => handleViewUsta(request)}
                            data-testid={`usta-info-${index}`}
                          >
                            <div className="flex items-center gap-4">
                              {request.usta_photo ? (
                                <img 
                                  src={request.usta_photo} 
                                  alt={request.usta_name}
                                  className="w-14 h-14 rounded-full object-cover border-2 border-blue-300"
                                />
                              ) : (
                                <div className="w-14 h-14 rounded-full bg-blue-200 flex items-center justify-center">
                                  <User className="w-7 h-7 text-blue-600" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-semibold text-[#264653]">{request.usta_name}</p>
                                <div className="flex items-center gap-3 text-sm">
                                  <a href={`tel:${request.usta_phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                    <Phone className="w-3.5 h-3.5" />
                                    {request.usta_phone}
                                  </a>
                                  {request.usta_rating > 0 && (
                                    <span className="flex items-center gap-1 text-amber-600">
                                      <Star className="w-3.5 h-3.5 fill-amber-500" />
                                      {request.usta_rating} ({request.usta_review_count})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs text-blue-500">Detay için tıklayın →</span>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {request.status === 'completed' && !request.has_reviewed && (
                          <Button
                            onClick={() => handleOpenReview(request)}
                            className="w-full bg-gradient-to-r from-[#E29578] to-[#F4A896] hover:from-[#D67A56] hover:to-[#E29578] text-white"
                            data-testid={`review-btn-${index}`}
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Ustayı Değerlendir
                          </Button>
                        )}
                        
                        {request.has_reviewed && (
                          <div className="flex items-center gap-2 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Bu iş için değerlendirme yaptınız
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#264653] flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Ustayı Değerlendir
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-[#E0F2F4] rounded-lg p-3">
                <p className="text-sm text-[#5F7D8B]">Usta</p>
                <p className="font-semibold text-[#264653]">{selectedRequest.usta_name}</p>
              </div>
              
              {/* Star Rating */}
              <div>
                <p className="text-sm text-[#5F7D8B] mb-2">Puanınız</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= reviewRating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Comment */}
              <div>
                <p className="text-sm text-[#5F7D8B] mb-2">Yorumunuz</p>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Deneyiminizi paylaşın..."
                  rows={4}
                  className="border-[#B3E0E6] focus:border-[#006D77]"
                />
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Yorumunuz admin onayından sonra yayınlanacaktır.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowReviewModal(false)}
                  className="flex-1 border-[#B3E0E6]"
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={reviewLoading || !reviewComment.trim()}
                  className="flex-1 bg-[#006D77] hover:bg-[#005861] text-white"
                >
                  {reviewLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Gönder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Usta Detail Modal */}
      <Dialog open={showUstaModal} onOpenChange={setShowUstaModal}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#264653]">Usta Bilgileri</DialogTitle>
          </DialogHeader>
          
          {selectedUsta && (
            <div className="text-center">
              {selectedUsta.photo ? (
                <img 
                  src={selectedUsta.photo} 
                  alt={selectedUsta.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-[#006D77] mx-auto mb-4"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#E0F2F4] flex items-center justify-center mx-auto mb-4">
                  <User className="w-12 h-12 text-[#006D77]" />
                </div>
              )}
              
              <h3 className="text-xl font-semibold text-[#264653] mb-1">{selectedUsta.name}</h3>
              
              {selectedUsta.rating > 0 && (
                <div className="flex items-center justify-center gap-1 mb-4">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="font-semibold text-[#264653]">{selectedUsta.rating}</span>
                  <span className="text-[#5F7D8B]">({selectedUsta.reviewCount} değerlendirme)</span>
                </div>
              )}
              
              <a 
                href={`tel:${selectedUsta.phone}`}
                className="inline-flex items-center gap-2 bg-[#006D77] hover:bg-[#005861] text-white px-6 py-3 rounded-full transition-all"
              >
                <Phone className="w-5 h-5" />
                {selectedUsta.phone}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
